import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { Sprint } from '../../../models/sprint';
import { SprintService } from '../../../services/sprint.service';
import { ProgramIncrement } from '../../../models/programIncrement';
import { ComponentEvent } from '../../../models/enums/componentEvent';
import { AwallComponentEvent } from '../../../models/awallComponentEvent';
import { Status } from '../../../models/status';
import { forkJoin } from 'rxjs';
import { Issue } from '../../../models/issue';
import { IssuecardComponent } from '../../issuecard/issuecard.component';
import { IssuecardHelper } from '../../issuecard/issuecard.helper';
import { UserstoryService } from '../../../services/userstory.service';
import { Userstory } from '../../../models/userstory';
import { MpcBoardSprint } from '../mpc-board-sprint/mpc-board-sprint.component';
import { DependencyEditmodeService } from '../../../services/dependency-editmode.service';
import { CommunicationService } from '../../../services/communication.service';
import { ProjectService } from '../../../services/project.service';
import { MpcBoardViewComponent } from '../../../actionviews/mpc-board-view/mpc-board-view.component';

@Component({
  selector: 'app-mpc-board-team',
  templateUrl: './mpc-board-team.component.html',
  styleUrls: ['./mpc-board-team.component.scss']
  })
/** This class represents a team view on the PI board. */
export class MpcBoardTeamComponent implements OnInit {
  @ViewChildren(MpcBoardSprint) mpcSprintBoards: QueryList<MpcBoardSprint>;
  @Input() teamKey: string;
  @Input() programIncrement: ProgramIncrement;
  sprints: Sprint[] = [];
  statuses: Status[] = [];
  @Output() loadingDataCompleted = new EventEmitter<string>();
  showHeader = false;

  constructor(
    private userstoryService: UserstoryService,
    private sprintService: SprintService,
    private projectService: ProjectService,
    private communicationService: CommunicationService,
    private issueCardHelper: IssuecardHelper
  ) { }

  ngOnInit() {
    this.loadSprints();
  }

  loadSprints(): void {
    forkJoin([
      this.sprintService.getSprintsForProgramIncrement(this.teamKey, this.programIncrement),
      this.projectService.getBoardStates(this.teamKey)
    ]).subscribe(([sprints, boardConfig]) => {
      this.sprints = sprints;
      this.sortSprintsByStartDate();
      this.statuses = boardConfig.getAllStatuses();
    });
  }

  private sortSprintsByStartDate(): void {
    this.sprints.sort((a, b) => {
      const startDateA = (new Date(a.startDate)).getTime();
      const startDateB = (new Date(b.startDate)).getTime();
      return startDateA - startDateB;
    });
  }

  // TODO is there a more elegant way to check if all sprint-boards are ready?
  onSprintBoardIsReady(sprintId: number): void {
    this.sprints.find(sprint => (sprint.id === sprintId))['sprintBoardReady'] = true;
    const allReady = this.sprints.every(sprint => (sprint['sprintBoardReady']));
    if (allReady) {
      this.loadingDataCompleted.emit(this.teamKey);
    }
  }

  /**
   * Sends an AwallComponentEvent to team MPCBoard telling it to remove this team from the
   * program and the board
   */
  removeTeam(): void {
    // Tell mpcboard to remove this team
    const data = { 'teamKey': this.teamKey };
    const event = new AwallComponentEvent(ComponentEvent.MPCBOARD_REMOVE_TEAM, data);
    this.communicationService.sendData(MpcBoardViewComponent.componentId, event);
  }

  /**
  * Open a dialog (IssueCard) for the creation of a new Issue
  */
  openNewIssueDialog(mouseEvent: MouseEvent, sprint: Sprint): void {
    this.issueCardHelper.openNewIssueDialog(
      this.teamKey,
      this.statuses,
      (issueCard: IssuecardComponent) => this.onCardClosed(issueCard),
      (issue: Issue) => this.onNewIssueCreated(issue, sprint),
      mouseEvent,
      null
    );
  }

  private onNewIssueCreated(issue: Issue, sprint: Sprint) {
    this.userstoryService.assignIssuesToSprint(this.teamKey, sprint.id.toString(), new Array(<Userstory>issue)).subscribe(
      () => {
        // Send an event to the sprintBoard to add the newly created issue
        const targetComponentId = MpcBoardSprint.getMPCSprintComponentId(this.teamKey, sprint.id);
        const addIssueEvent = new AwallComponentEvent(ComponentEvent.MPCBOARD_SPRINT_ADD_ISSUE, { issue, sprint });
        this.communicationService.sendData(targetComponentId, addIssueEvent);
        // Send an event to Editmode so it can reapply the edit-mode-style on the new issue
        const componentEvent = new AwallComponentEvent(ComponentEvent.MPCBOARD_CHANGED);
        this.communicationService.sendData(DependencyEditmodeService.componentId, componentEvent);
      }
    );
  }

  private onCardClosed(card: IssuecardComponent) {
    this.issueCardHelper.removeCardFromGlobalReferences(card);
  }
}
